import { normalizeYouTubeTrailer, normalizeVkTrailer } from '@/utils/kinobdMedia.js'

const SHIKIMORI_GRAPHQL_URL = 'https://shikimori.io/api/graphql'
const TRAILER_KINDS = new Set(['pv', 'character_trailer'])
const KIND_PRIORITY = { pv: 0, character_trailer: 1 }
const PROVIDER_PRIORITY = { vk: 0, youtube: 1 }
const LOW_VALUE_TRAILER_NAME = /(?:тизер|teaser|анонс|announcement|character\s+voice)/iu
const PERSONAL_TRAILER_TIMEOUT = 12000
const personalTrailerRequests = new Map()

const PERSONAL_ANIME_TRAILER_QUERY = `
    query PersonalAnimeTrailer($ids: String!) {
        animes(ids: $ids, limit: 1) {
            id
            videos {
                id
                name
                kind
                playerUrl
                imageUrl
            }
        }
    }
`

function normalizeHttpsUrl(value) {
    if (!value) return null

    try {
        const normalized = String(value).startsWith('//')
            ? `https:${value}`
            : String(value).replace(/^http:/i, 'https:')
        const url = new URL(normalized)

        if (url.protocol !== 'https:') return null
        return url.toString()
    } catch (_) {
        return null
    }
}

export function buildVkEmbedUrl(value) {
    return normalizeVkTrailer({ kind: 'pv', playerUrl: value })?.embedUrl || null
}

export function normalizeTrailerCandidate(video) {
    const kind = String(video?.kind || '').toLowerCase()
    const name = String(video?.name || '').trim()
    if (!TRAILER_KINDS.has(kind) || LOW_VALUE_TRAILER_NAME.test(name)) return null

    const sourceUrl = video?.playerUrl || video?.embedUrl
    const youtube = normalizeYouTubeTrailer(sourceUrl)
    if (youtube) {
        return {
            ...youtube,
            id: String(video.id || youtube.id),
            kind,
            poster: normalizeHttpsUrl(video.imageUrl || video.poster) || youtube.poster,
        }
    }

    const vk = normalizeVkTrailer({ ...video, playerUrl: sourceUrl })
    if (!vk) return null
    return {
        ...vk,
        id: String(video.id || vk.id),
        kind,
        poster: normalizeHttpsUrl(video.imageUrl || video.poster)
    }
}

async function requestAnimeTrailersByShikimoriId(shikimoriId, { signal } = {}) {
    const normalizedId = String(shikimoriId || '').trim()
    if (!/^\d+$/.test(normalizedId) || Number(normalizedId) <= 0) return []

    const controller = new AbortController()
    const abortRequest = () => controller.abort()
    if (signal?.aborted) abortRequest()
    else signal?.addEventListener('abort', abortRequest, { once: true })
    const timeout = setTimeout(abortRequest, PERSONAL_TRAILER_TIMEOUT)

    let payload
    try {
        const response = await fetch(SHIKIMORI_GRAPHQL_URL, {
            method: 'POST',
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                query: PERSONAL_ANIME_TRAILER_QUERY,
                variables: { ids: normalizedId }
            }),
            signal: controller.signal
        })
        if (!response.ok) throw new Error(`Shikimori returned ${response.status}`)
        payload = await response.json()
    } finally {
        clearTimeout(timeout)
        signal?.removeEventListener('abort', abortRequest)
    }
    if (payload.errors?.length) {
        throw new Error(payload.errors[0].message || 'Shikimori GraphQL error')
    }

    const anime = (payload.data?.animes || [])
        .find((candidate) => String(candidate?.id || '') === normalizedId)
    if (!anime) return []

    const seen = new Set()
    return (anime.videos || [])
        .map(normalizeTrailerCandidate)
        .filter(Boolean)
        .sort((left, right) => PROVIDER_PRIORITY[left.provider] - PROVIDER_PRIORITY[right.provider]
            || KIND_PRIORITY[left.kind] - KIND_PRIORITY[right.kind])
        .filter((candidate) => {
            if (seen.has(candidate.embedUrl)) return false
            seen.add(candidate.embedUrl)
            return true
        })
        .slice(0, 6)
}

export function fetchAnimeTrailersByShikimoriId(shikimoriId, options = {}) {
    const normalizedId = String(shikimoriId || '').trim()
    if (!/^\d+$/.test(normalizedId) || Number(normalizedId) <= 0) return Promise.resolve([])
    if (options.signal) return requestAnimeTrailersByShikimoriId(normalizedId, options)

    if (personalTrailerRequests.has(normalizedId)) {
        return personalTrailerRequests.get(normalizedId)
    }
    const request = requestAnimeTrailersByShikimoriId(normalizedId)
        .finally(() => {
            if (personalTrailerRequests.get(normalizedId) === request) {
                personalTrailerRequests.delete(normalizedId)
            }
        })
    personalTrailerRequests.set(normalizedId, request)
    return request
}

export async function fetchAnimeTrailerByShikimoriId(shikimoriId, options = {}) {
    return (await fetchAnimeTrailersByShikimoriId(shikimoriId, options))[0] || null
}
